import { ProjectRegexFilter } from "./api/types";

export const brandFilterName = "Brand";
export const nonBrandFilterName = "Non-Brand";

export function isManagedBrandFilter(name: string): boolean {
  return name.trim() === brandFilterName || name.trim() === nonBrandFilterName;
}

// A project can open Keyword Map once the brand pattern is stored.
export function hasBrandExclude(brandQueries: string[] | undefined | null): boolean {
  return (brandQueries ?? []).some((query) => query.trim() !== "");
}

// Brand includes the pattern. Non-Brand excludes the same pattern.
// An empty pattern removes both. They do not count toward the custom-filter limit.
export function brandManagedFilters(pattern: string): ProjectRegexFilter[] {
  const expression = pattern.trim();
  if (expression === "") {
    return [];
  }
  return [
    {
      name: brandFilterName,
      target: "query",
      operator: "includingRegex",
      expression,
    },
    {
      name: nonBrandFilterName,
      target: "query",
      operator: "excludingRegex",
      expression,
    },
  ];
}

// Query dropdowns offer Brand and Non-Brand ahead of the project's other query regexes.
// An empty pattern leaves those other regexes unchanged. A stored Brand or Non-Brand
// name is replaced by the pattern so the same filter is not listed twice.
export function queryFiltersIncludingBrand(
  filters: ProjectRegexFilter[] | undefined,
  brandPattern: string
): ProjectRegexFilter[] {
  const queryFilters = (Array.isArray(filters) ? filters : []).filter(
    (filter) =>
      filter.target === "query" &&
      filter.name.trim() !== "" &&
      filter.expression.trim() !== ""
  );
  const fromBrand = brandManagedFilters(brandPattern);
  const managed =
    fromBrand.length > 0
      ? fromBrand
      : queryFilters.filter((filter) => isManagedBrandFilter(filter.name));
  const rest = queryFilters.filter((filter) => !isManagedBrandFilter(filter.name));
  const seen = new Set<string>();
  return [...managed, ...rest].filter((filter) => {
    const name = filter.name.trim();
    if (seen.has(name)) {
      return false;
    }
    seen.add(name);
    return true;
  });
}

// Brand and Non-Brand fill the query field with the project pattern.
// The separate "exclude brand" switch would cancel Brand or repeat Non-Brand.
export function brandPatternSelection(
  operator: string,
  expression: string,
  brandPattern: string
): boolean {
  const pattern = brandPattern.trim();
  if (pattern === "" || expression.trim() !== pattern) {
    return false;
  }
  return operator === "includingRegex" || operator === "excludingRegex";
}

// A typed period is a literal dot. Leave an already escaped "\." alone, and
// leave "." when it is the "any character" atom (".*", ".+", ".?", ".{").
export function escapeLiteralDots(pattern: string): string {
  let result = "";
  for (let index = 0; index < pattern.length; index += 1) {
    const char = pattern[index];
    if (char === "\\") {
      result += char;
      if (index + 1 < pattern.length) {
        result += pattern[index + 1];
        index += 1;
      }
      continue;
    }
    if (char === ".") {
      const next = pattern[index + 1];
      if (next === "*" || next === "+" || next === "?" || next === "{") {
        result += char;
        continue;
      }
      result += "\\.";
      continue;
    }
    result += char;
  }
  return result;
}

// Stored brand queries are joined into the regex the user edits.
// Periods are shown escaped, including patterns that were typed without "\.".
export function brandPatternFromStored(terms: string[] | undefined): string {
  const pattern = (terms || [])
    .map((term) => term.trim())
    .filter(Boolean)
    .join("|");
  return escapeLiteralDots(pattern);
}

// The field is one regex. Keep it as a single value so "|" and commas survive.
// Escape typed periods so the saved pattern displays and matches "mobil\.de".
export function storedBrandQueriesFromPattern(raw: string): string[] {
  const pattern = escapeLiteralDots(raw.trim());
  return pattern === "" ? [] : [pattern];
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Append a clicked query as a literal alternative. The rest of the pattern stays untouched.
export function appendBrandAlternative(raw: string, query: string): string {
  const literal = escapeRegexLiteral(query.trim().toLowerCase());
  if (literal === "") {
    return raw.trim();
  }
  const pattern = raw.trim();
  if (pattern === "") {
    return literal;
  }
  const alternatives = pattern.split("|");
  if (alternatives.some((part) => part === literal)) {
    return pattern;
  }
  return `${pattern}|${literal}`;
}

export function removeBrandAlternative(raw: string, query: string): string {
  const literal = escapeRegexLiteral(query.trim().toLowerCase());
  const plain = query.trim().toLowerCase();
  return raw
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part !== "" && part !== literal && part !== plain)
    .join("|");
}

export type ClusterableQuery = {
  keys?: string[];
};

// Split already-fetched queries with the user's brand regex.
// Live clustering matches anywhere in the query (unanchored), like a regex search.
// The stored form value is not rewritten — only this in-memory test changes.
export function clusterBrandQueries<T extends ClusterableQuery>(
  queries: T[],
  pattern: string
): { includes: T[]; excludes: T[] } {
  const trimmed = pattern.trim();
  // Empty pattern: every row is non-brand.
  if (trimmed === "") {
    return { includes: queries, excludes: [] };
  }

  let re: RegExp;
  try {
    // Unanchored: "null leasing" matches "null leasing angebote".
    // Patterns that already use ".*" keep working.
    re = new RegExp(trimmed);
  } catch {
    // Invalid pattern while typing: treat as no match (all non-brand).
    return { includes: queries, excludes: [] };
  }

  const includes: T[] = [];
  const excludes: T[] = [];
  for (const row of queries) {
    const query = row.keys?.[0] || "";
    if (re.test(query)) {
      excludes.push(row);
    } else {
      includes.push(row);
    }
  }
  return { includes, excludes };
}
